import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Grid,
    Typography,
    TextField,
    InputAdornment,
} from '@material-ui/core/';
import EditIcon from '@material-ui/icons/EditTwoTone';
import { withStyles } from '@material-ui/core/styles';
import EnhancedTable from '../common/EnhancedTable/EnhancedTable';
import MultiSelect from '../common/MultiSelect/MultiSelect';
import GridCard from '../common/GridCard/GridCard';
import ButtonSwitch from '../common/ButtonSwitch/ButtonSwitch';
import SubHeader from '../common/SubHeader/SubHeader';
import translationPropType from '../../proptypes/translationPropType';
import {
    getCompanyReportStats,
    setShowGraphs,
    setShowCustomerContacts,
    setShowImpressions,
    setShowPerHid,
    setReportHeader,
    searchOrderStatistics,
    setNotGroupedProducts,
} from '../../reduxStore/config/companyreport';

const CompanyReportSettings = ({
    theme,
    translation,
    ...rest
}) => {
    const dispatch = useDispatch();

    const {
        companyreport: {
            formState,
            reportHeader = translation.CONTACT_AND_EXPO_STATS,
            showGraphs,
            showCustomerContacts,
            showImpressions,
            showPerHid,
            notGroupedProducts,
            productSummary,
            companyCount,
            selectCompanyTableData,
        },
        user: {
            user,
        },
    } = useSelector(state => state);

    const isAdvancedUser = user ? user.isAdvancedUser : false;

    const productSelectValues = productSummary ? productSummary
        .filter(summary => summary.lead > 0)
        .map(summary => summary.name)
        .reduce((result, id) => {
            result.push(
                {
                    id,
                    text: translation.PRODUCT_KEYS[id] || id,
                },
            );
            return result;
        }, []) : [];

    const selectedProducts = productSelectValues
        .filter(product => notGroupedProducts.includes(product.id));

    const searchSelectedCompanies = (selectedIds) => {
        const newFormState = Object.assign({}, formState, { HID: selectedIds.join(','), searchType: 'HID' });
        dispatch(getCompanyReportStats(newFormState));
    };

    const handleMultiSelectEvent = (dataArray) => {
        dispatch(setNotGroupedProducts(dataArray.map(data => data.id)));
    };

    const handleUpdateReportHeader = (e) => {
        dispatch(setReportHeader(e.target.value));
    };

    const handleSearchOrderStatistics = orderId => () => {
        dispatch(searchOrderStatistics(orderId));
    };

    return (
        <GridCard xs={12} lg={10} xl={6} highlighted {...rest}>
            <SubHeader header={translation.REPORT_SETTINGS} />
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <TextField
                        label={translation.HEADER}
                        value={reportHeader || ''}
                        name="reportHeader"
                        onChange={handleUpdateReportHeader}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    <EditIcon color="primary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MultiSelect
                        allValues={productSelectValues}
                        selectedValues={selectedProducts}
                        label={translation.CUSTOMER_CONTACTS_SHOWN_IN_TABLE}
                        placeholder={translation.ALL}
                        handleSelect={handleMultiSelectEvent}
                        fullWidth
                        labelBackgroundColor={
                            theme.palette.background.highlightedCard
                        }
                    />
                </Grid>
                <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <Grid container wrap="wrap">
                        <ButtonSwitch
                            checked={showCustomerContacts}
                            label={translation.CUSTOMER_CONTACTS}
                            name="showCustomerContacts"
                            onChange={() => dispatch(setShowCustomerContacts())}
                        />
                        <ButtonSwitch
                            checked={showImpressions}
                            label={translation.IMPRESSIONS}
                            name="showImpressions"
                            onChange={() => dispatch(setShowImpressions())}
                        />
                        <ButtonSwitch
                            checked={showGraphs}
                            label={translation.SHOW_GRAPHS}
                            name="showGraphs"
                            onChange={() => dispatch(setShowGraphs())}
                        />
                        {isAdvancedUser && (
                            <ButtonSwitch
                                checked={showPerHid}
                                label={translation.SHOW_PER_COMPANY}
                                name="showPerHid"
                                onChange={() => dispatch(setShowPerHid())}
                            />
                        )}
                    </Grid>
                </Grid>
                {selectCompanyTableData && (
                    <Grid item xs={12}>
                        <Typography display="inline" variant="h4">
                            {`${companyCount} `}
                        </Typography>
                        <Typography display="inline" variant="body2">
                            {translation.COMPANIES_INCLUDED_IN_REPORT}
                        </Typography>
                        <EnhancedTable
                            tableActionText={translation.SEARCH}
                            tableActionTooltip={
                                translation.TOOLTIP_SEARCH_COMPANIES
                            }
                            tableActionHelpText={
                                translation.HELPTEXT_SELECT_COMPANIES_FOR_REPORT
                            }
                            handleTableAction={searchSelectedCompanies}
                            rows={selectCompanyTableData.dataRows}
                            cols={selectCompanyTableData.dataCols}
                            collapsible
                            initiallyCollapsed
                            collapseText={translation.SELECT_COMPANIES}
                            selectableRows
                            translation={translation}
                            actionMethods={{ orderId: handleSearchOrderStatistics }}
                        />
                    </Grid>
                )}
            </Grid>
        </GridCard>
    );
};

CompanyReportSettings.propTypes = {
    theme: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
};

export default withStyles({}, { withTheme: true })(CompanyReportSettings);
